/**
 * Keeps the upload tray alive across a page reload or browser crash.
 *
 * The tray (what is uploading, what finished, what failed) used to live only
 * in memory and was deliberately wiped on every reload, so after a crash the
 * user could not tell which of hundreds of claim files had made it. This
 * module saves a small, file-free description of the tray to the browser and
 * rebuilds it on the next load.
 *
 * What cannot survive a reload is the file itself: a browser never lets a
 * page re-read a file from disk without the user picking it again. So every
 * file that was still sending when the page went away comes back as
 * "interrupted", carrying the client, folder and session it was headed for,
 * and `matchPickedFiles` lines the user's re-picked files back up with them.
 */

export const TRAY_STORAGE_VERSION = 1;
const STORAGE_PREFIX = 'whg.uploadTray.v1.';
// A claim-file day can be a few thousand files. Each saved entry is a few
// hundred bytes, so this stays well under the ~5 MB browser limit while
// covering a very large batch. Unfinished work is always kept in preference
// to finished work when the cap bites.
export const MAX_SAVED_ENTRIES = 4000;
// A tray older than this is a stale memory, not a recovery aid.
export const MAX_SAVED_AGE_MS = 3 * 24 * 60 * 60 * 1000;

export const INTERRUPTED_STATUS = 'interrupted';
export const INTERRUPTED_ERROR_TYPE = 'Interrupted';
export const INTERRUPTED_MESSAGE =
  'The page reloaded before this file finished sending. Pick the file again to finish it.';

const getStorage = () => {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
};

/** Storage key for the signed-in user, or null when nobody is signed in. */
export const getTrayStorageKey = () => {
  const storage = getStorage();
  if (!storage) return null;
  let userId;
  try {
    userId = storage.getItem('user_id');
  } catch {
    return null;
  }
  if (!userId || typeof userId !== 'string') return null;
  return `${STORAGE_PREFIX}${userId}`;
};

// Only the matter fields the upload helpers read back: the matter id (in any
// of its three spellings), whether this is a folder record, and the display
// name used to label the client in the tray.
const slimMatterDetails = (matterDetails) => {
  if (!matterDetails || typeof matterDetails !== 'object') return undefined;
  const slim = {};
  for (const key of [
    'id',
    'sf_id',
    'sf_matter_id',
    'folder',
    'matter_displayName',
    'display_name',
  ]) {
    if (matterDetails[key] !== undefined && matterDetails[key] !== null) {
      slim[key] = matterDetails[key];
    }
  }
  return slim;
};

/**
 * The destination of one file, without the file: enough to send it to the
 * same matter, folder and upload session once the user picks it again.
 */
export const AI_ZIP_KIND = 'ai-zip';

export const buildResume = ({
  matterDetails,
  uniqueId,
  newly_created = false,
  relativePath = null,
  overrideExisting = false,
  batchId = null,
  aiProcess = false,
  // A big zip sent for server-side unpacking is resumed through its own
  // flow, which needs the matter and the bare destination-folder id.
  kind,
  matterId,
  folderId,
} = {}) => ({
  matterDetails: slimMatterDetails(matterDetails),
  uniqueId: uniqueId ?? null,
  newly_created: Boolean(newly_created),
  relativePath: relativePath ?? null,
  overrideExisting: Boolean(overrideExisting),
  batchId: batchId ?? null,
  aiProcess: Boolean(aiProcess),
  ...(kind ? { kind, matterId: matterId ?? null, folderId: folderId ?? null } : {}),
});

const resumeFrom = (entry) => {
  if (entry?.resume) return buildResume(entry.resume);
  if (entry?.retryContext) return buildResume(entry.retryContext);
  return undefined;
};

/** A tray entry reduced to plain, file-free data safe to store as JSON. */
export const slimEntry = (entry) => {
  const slim = {
    id: entry.id,
    fileName: entry.fileName,
    fileSize: typeof entry.fileSize === 'number' ? entry.fileSize : undefined,
    type: entry.type,
    status: entry.status,
    // Moment-to-moment progress is noise once the page is gone; keeping it
    // out also lets identical states serialize identically, so a busy batch
    // does not rewrite storage on every progress tick.
    progress: entry.status === 'completed' ? 100 : 0,
    uploadIndex: entry.uploadIndex,
    totalUploads: entry.totalUploads,
    batchId: entry.batchId,
    matterId: entry.matterId,
    matterLabel: entry.matterLabel,
    skipOptimization: entry.skipOptimization ? true : undefined,
    // The name as picked: the tray rewrites fileName with progress text
    // ("… extracting 12/300"), so matching a re-picked file uses this.
    matchName: entry.matchName,
    // A zip whose bytes all arrived and is being unpacked by the server. A
    // reload does not stop that, so it is checked on, never re-sent.
    serverJobId: entry.serverJobId,
    error: entry.error,
    errorType: entry.errorType,
    resume: resumeFrom(entry),
  };
  for (const key of Object.keys(slim)) {
    if (slim[key] === undefined) delete slim[key];
  }
  return slim;
};

const isFinished = (entry) => entry.status === 'completed';

// Keep unfinished work first; trim finished work (oldest first) if over cap.
const capEntries = (uploads, failedUploads, cap) => {
  const total = uploads.length + failedUploads.length;
  if (total <= cap) return { uploads, failedUploads };
  const unfinished = uploads.filter((u) => !isFinished(u));
  const finished = uploads.filter(isFinished);
  const room = Math.max(0, cap - unfinished.length - failedUploads.length);
  return {
    uploads: [...unfinished, ...finished.slice(finished.length - room)],
    failedUploads,
  };
};

/** Writes the tray; an empty tray removes the saved copy. Never throws. */
export const saveTray = ({ uploads = [], failedUploads = [] }, key = getTrayStorageKey()) => {
  const storage = getStorage();
  if (!storage || !key) return false;
  try {
    if (uploads.length === 0 && failedUploads.length === 0) {
      storage.removeItem(key);
      return true;
    }
    const attempt = (cap) => {
      const capped = capEntries(uploads, failedUploads, cap);
      storage.setItem(
        key,
        JSON.stringify({
          v: TRAY_STORAGE_VERSION,
          savedAt: Date.now(),
          uploads: capped.uploads.map(slimEntry),
          failedUploads: capped.failedUploads.map(slimEntry),
        })
      );
    };
    try {
      attempt(MAX_SAVED_ENTRIES);
    } catch {
      // Storage full: fall back to a much smaller copy rather than lose the
      // list of what still needs doing.
      attempt(Math.floor(MAX_SAVED_ENTRIES / 8));
    }
    return true;
  } catch {
    return false;
  }
};

export const clearSavedTray = (key = getTrayStorageKey()) => {
  const storage = getStorage();
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    // Nothing useful to do if storage is unavailable.
  }
};

/**
 * Reads the saved tray and turns it into live tray state.
 *
 * Finished uploads come back finished. Anything that was still sending
 * becomes an "interrupted" failure carrying its destination, since its bytes
 * are gone. Returns null when there is nothing worth restoring.
 */
export const loadTray = (key = getTrayStorageKey(), now = Date.now()) => {
  const storage = getStorage();
  if (!storage || !key) return null;
  let saved;
  try {
    const raw = storage.getItem(key);
    if (!raw || typeof raw !== 'string') return null;
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!saved || saved.v !== TRAY_STORAGE_VERSION) return null;
  if (typeof saved.savedAt === 'number' && now - saved.savedAt > MAX_SAVED_AGE_MS) {
    clearSavedTray(key);
    return null;
  }

  const uploads = [];
  const failedUploads = [];
  for (const entry of Array.isArray(saved.uploads) ? saved.uploads : []) {
    if (!entry || !entry.id) continue;
    const pickedName = entry.matchName || entry.fileName;
    if (entry.serverJobId && !isFinished(entry)) {
      // Fully sent; the server is unpacking it. Not interrupted: the tray
      // checks on the job and reports how it ended.
      uploads.push({
        ...entry,
        fileName: `${pickedName} — checking progress…`,
        progress: 100,
        status: 'uploading',
        restored: true,
      });
    } else if (isFinished(entry)) {
      // Fast-web-view optimization progress is not tracked across a reload,
      // so a restored finished file must not sit on "Optimizing files…".
      uploads.push({ ...entry, progress: 100, status: 'completed', skipOptimization: true });
    } else if (entry.type !== 'folder') {
      // Empty-folder placeholders have no bytes to re-send.
      failedUploads.push({
        ...entry,
        fileName: pickedName,
        progress: 0,
        status: INTERRUPTED_STATUS,
        errorType: INTERRUPTED_ERROR_TYPE,
        error: INTERRUPTED_MESSAGE,
      });
    }
  }
  for (const entry of Array.isArray(saved.failedUploads) ? saved.failedUploads : []) {
    if (!entry || !entry.id) continue;
    failedUploads.push(entry);
  }

  if (uploads.length === 0 && failedUploads.length === 0) return null;
  return { uploads, failedUploads };
};

export const isInterruptedUpload = (entry) =>
  entry?.status === INTERRUPTED_STATUS && Boolean(entry?.resume);

const entrySize = (entry) => (typeof entry?.fileSize === 'number' ? entry.fileSize : null);

const pickedPath = (file) => file?.webkitRelativePath || file?.customRelativePath || '';

/**
 * Lines up files the user just re-picked with the interrupted entries.
 *
 * A file matches an entry with the same name and size. When the user picked a
 * whole folder (so the browser reports each file's folder path) a matching
 * relative path wins over a bare name match, so two same-named files in
 * different sub-folders each find their own entry. Each entry is used once.
 *
 * Returns the matches plus the picked files that matched nothing and the
 * interrupted entries still waiting for their file.
 */
export const matchPickedFiles = (interrupted, pickedFiles) => {
  const pool = [...(interrupted || [])];
  const matches = [];
  const unmatchedFiles = [];

  for (const wrapped of Array.from(pickedFiles || [])) {
    const file = wrapped?.file || wrapped;
    if (!file) continue;
    const candidates = pool
      .map((entry, index) => ({ entry, index }))
      .filter(
        ({ entry }) =>
          (entry.matchName || entry.fileName) === file.name &&
          (entrySize(entry) === null || entrySize(entry) === file.size)
      );
    if (candidates.length === 0) {
      unmatchedFiles.push(file);
      continue;
    }
    const path = pickedPath(wrapped) || pickedPath(file);
    const exactPath = path
      ? candidates.find(({ entry }) => entry.resume?.relativePath === path)
      : null;
    const chosen = exactPath || candidates[0];
    pool.splice(chosen.index, 1);
    matches.push({ entry: chosen.entry, file });
  }

  return { matches, unmatchedFiles, stillWaiting: pool };
};

/** Rebuilds the in-memory retry data for an entry from its saved destination. */
export const retryContextFor = (entry, file) => ({
  file,
  matterDetails: entry.resume?.matterDetails,
  uniqueId: entry.resume?.uniqueId ?? null,
  newly_created: Boolean(entry.resume?.newly_created),
  relativePath: entry.resume?.relativePath ?? null,
  overrideExisting: Boolean(entry.resume?.overrideExisting),
  batchId: entry.resume?.batchId ?? entry.batchId ?? null,
  aiProcess: Boolean(entry.resume?.aiProcess),
});
